import React from 'react';
import { KeywordResearchProject } from '../../types';
import { Badge } from '../UI/Badge';
import { FileText, Download } from 'lucide-react';
import { Button } from '../UI/Button';
import { KeywordPdfService } from '../../services/keywordPdfService';

interface LiveReportPanelProps {
  project: KeywordResearchProject;
}

export const LiveReportPanel: React.FC<LiveReportPanelProps> = ({ project }) => {
  return (
    <div className="glass-panel" style={{ padding: '24px', height: '100%', overflowY: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '12px', borderBottom: '1px solid var(--border-color)' }}>
        <div>
          <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--accent-green)', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <FileText size={14} />
            <span>Live Continuous Report Builder</span>
          </div>
          <h3 style={{ fontSize: '16px', color: '#ffffff', fontWeight: 800, margin: '2px 0 0 0' }}>
            Keyword Research Report — {project.clientName}
          </h3>
        </div>
        <Button
          size="sm"
          variant="primary"
          icon={<Download size={14} />}
          onClick={() => KeywordPdfService.exportToPdf(project)}
        >
          Export Report PDF
        </Button>
      </div>

      {/* SECTION 1: CLIENT & BUSINESS OVERVIEW */}
      <div style={{ marginBottom: '24px', background: 'rgba(2, 6, 23, 0.6)', padding: '14px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-color)' }}>
        <h4 style={{ fontSize: '13px', color: 'var(--accent-blue)', fontWeight: 700, margin: '0 0 8px 0' }}>
          Section 1: Client & Business Overview
        </h4>
        <div style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.6 }}>
          <strong>Business Name:</strong> {project.businessProfile.businessName || 'Not Set'}<br />
          <strong>Website:</strong> {project.websiteUrl}<br />
          <strong>Target Location:</strong> {project.businessProfile.primaryLocation || 'Not Set'}<br />
          <strong>Business Model:</strong> {project.businessProfile.businessType}<br />
          <strong>Confirmed Services:</strong> {project.businessProfile.confirmedServices.join(', ') || 'None Added'}
        </div>
      </div>

      {/* SECTION 2: SEED KEYWORDS */}
      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ fontSize: '13px', color: 'var(--accent-blue)', fontWeight: 700, margin: '0 0 8px 0' }}>
          Section 2: Core Seed Keywords ({project.seedKeywords.length})
        </h4>
        {project.seedKeywords.length === 0 ? (
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic' }}>No seed keywords established yet.</div>
        ) : (
          <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '12px', color: '#ffffff', lineHeight: 1.6 }}>
            {project.seedKeywords.map(s => (
              <li key={s.id}>
                <strong>{s.seedKeyword}</strong> ({s.location}) — <Badge variant="lecture">{s.intent}</Badge>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* SECTION 3: KEYWORD VARIATIONS */}
      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ fontSize: '13px', color: 'var(--accent-blue)', fontWeight: 700, margin: '0 0 8px 0' }}>
          Section 3: Keyword Variations ({project.variations.length})
        </h4>
        {project.variations.length === 0 ? (
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic' }}>No keyword variations researched yet.</div>
        ) : (
          <div style={{ fontSize: '12px' }}>
            {project.variations.map(v => (
              <div key={v.id} style={{ background: 'rgba(15, 23, 42, 0.6)', padding: '8px 12px', borderRadius: '4px', marginBottom: '6px', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div style={{ fontWeight: 700, color: '#ffffff' }}>{v.keyword}</div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                  Variation of: <em>"{v.variationOf}"</em> | Vol: <strong>{v.searchVolumeStatus === 'collected' ? (v.searchVolume ?? 0) : 'Not collected yet'}</strong> | Status: <Badge variant={v.verificationStatus === 'user_verified' ? 'complete' : 'lecture'}>{v.verificationStatus === 'user_verified' ? 'Verified' : 'AI-Assisted — Unverified'}</Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* SECTION 4: CLIENT WEBSITE KEYWORDS */}
      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ fontSize: '13px', color: 'var(--accent-blue)', fontWeight: 700, margin: '0 0 8px 0' }}>
          Section 4: Keywords Found on Client Website ({project.websiteContentKeywords.length})
        </h4>
        {project.websiteContentKeywords.length === 0 ? (
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic' }}>No website keywords extracted yet.</div>
        ) : (
          <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '12px', color: '#ffffff', lineHeight: 1.6 }}>
            {project.websiteContentKeywords.map(w => (
              <li key={w.id}>
                <strong>{w.keyword}</strong> (Found in {w.whereFound} on {w.sourcePage})
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* SECTION 5: COMPETITOR KEYWORD FINDINGS */}
      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ fontSize: '13px', color: 'var(--accent-blue)', fontWeight: 700, margin: '0 0 8px 0' }}>
          Section 5: Competitor Keywords ({project.competitorKeywords.length})
        </h4>
        {project.competitorKeywords.length === 0 ? (
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic' }}>No competitor keywords analyzed yet.</div>
        ) : (
          <div style={{ fontSize: '12px' }}>
            {project.competitorKeywords.map(c => (
              <div key={c.id} style={{ padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <span style={{ fontWeight: 700, color: '#ffffff' }}>{c.keyword}</span> ({c.competitorName}) —{' '}
                <Badge variant={c.clientOffersThis ? 'complete' : 'lecture'}>
                  {c.clientOffersThis ? 'Client Offers Service' : 'REJECTED (Unsupported Service)'}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* SECTION 6: TOOL KEYWORDS */}
      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ fontSize: '13px', color: 'var(--accent-blue)', fontWeight: 700, margin: '0 0 8px 0' }}>
          Section 6: Tool-Discovered Keywords ({project.toolKeywords.length})
        </h4>
        {project.toolKeywords.length === 0 ? (
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic' }}>No tool keywords added yet.</div>
        ) : (
          <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '12px', color: '#ffffff', lineHeight: 1.6 }}>
            {project.toolKeywords.map(t => (
              <li key={t.id}>
                <strong>{t.keyword}</strong> via {t.sourceTool} (Vol: {t.searchVolumeStatus === 'collected' ? t.searchVolume : 'Not collected yet'})
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* SECTION 7: PRIMARY / SECONDARY / TERTIARY */}
      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ fontSize: '13px', color: 'var(--accent-green)', fontWeight: 700, margin: '0 0 8px 0' }}>
          Section 7: Primary / Secondary / Tertiary Target Classification
        </h4>
        {project.masterKeywords.filter(m => m.priority === 'Primary' || m.priority === 'Secondary' || m.priority === 'Tertiary').length === 0 ? (
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic' }}>Keywords not classified yet. Complete search volume and sorting steps.</div>
        ) : (
          <div style={{ fontSize: '12px' }}>
            {project.masterKeywords
              .filter(m => m.priority === 'Primary' || m.priority === 'Secondary' || m.priority === 'Tertiary')
              .map(m => (
                <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <span style={{ fontWeight: 700, color: '#ffffff' }}>{m.keyword}</span>
                  <Badge variant={m.priority === 'Primary' ? 'complete' : 'lecture'}>{m.priority}</Badge>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* SECTION 8: AEO & QUESTION OPPORTUNITIES */}
      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ fontSize: '13px', color: 'var(--accent-blue)', fontWeight: 700, margin: '0 0 8px 0' }}>
          Section 8: AEO & Question Research ({project.aeoQuestions.length})
        </h4>
        {project.aeoQuestions.length === 0 ? (
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic' }}>No questions researched yet.</div>
        ) : (
          <div style={{ fontSize: '12px' }}>
            {project.aeoQuestions.map(q => (
              <div key={q.id} style={{ padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <div style={{ fontWeight: 700, color: '#ffffff' }}>Q: {q.question}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '11px' }}>
                  Source: {q.source} | Direct Answer: <em>"{q.conciseAnswer || 'Not written yet'}"</em>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* SECTION 9: SEO METADATA & OPENGRAPH (STEP 12) */}
      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ fontSize: '13px', color: '#38bdf8', fontWeight: 700, margin: '0 0 8px 0' }}>
          Section 9: Finalized Page SEO Metadata (Step 12)
        </h4>
        <div style={{ fontSize: '11px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ background: 'rgba(15, 23, 42, 0.6)', padding: '8px 10px', borderRadius: '4px', border: '1px solid rgba(56, 189, 248, 0.2)' }}>
            <div style={{ fontWeight: 700, color: '#ffffff', marginBottom: '2px' }}>🏠 Homepage Target Metadata</div>
            <div style={{ color: '#38bdf8', fontWeight: 700 }}>Title: {project.homepageSeoTitle || 'Not finalized yet'} ({project.homepageSeoTitle?.length || 0} chars)</div>
            <div style={{ color: '#34d399', fontSize: '10px', marginTop: '2px' }}>Meta: {project.homepageMetaDescription || 'Not finalized yet'} ({project.homepageMetaDescription?.length || 0} chars)</div>
          </div>

          {(project.servicePageMappings || []).map((sp, idx) => (
            <div key={sp.id || idx} style={{ background: 'rgba(15, 23, 42, 0.6)', padding: '8px 10px', borderRadius: '4px', border: '1px solid rgba(255, 255, 255, 0.05)' }}>
              <div style={{ fontWeight: 700, color: '#ffffff', marginBottom: '2px' }}>📄 Page #{idx + 1}: {sp.pageName} ({sp.targetUrl})</div>
              <div style={{ color: '#38bdf8', fontWeight: 700 }}>Title: {sp.seoTitle || 'Not finalized yet'} ({sp.seoTitle?.length || 0} chars)</div>
              <div style={{ color: '#34d399', fontSize: '10px', marginTop: '2px' }}>Meta: {sp.metaDescription || 'Not finalized yet'} ({sp.metaDescription?.length || 0} chars)</div>
            </div>
          ))}
        </div>
      </div>

      {/* SECTION 10: DECISION & STRATEGY AUDIT LOG */}
      <div>
        <h4 style={{ fontSize: '13px', color: 'var(--accent-yellow)', fontWeight: 700, margin: '0 0 8px 0' }}>
          Section 10: Strategy Decision Log ({project.decisionLog.length})
        </h4>
        {project.decisionLog.length === 0 ? (
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic' }}>No decision entries logged yet.</div>
        ) : (
          <div style={{ fontSize: '11px' }}>
            {project.decisionLog.slice(0, 5).map(d => (
              <div key={d.id} style={{ padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <strong style={{ color: 'var(--accent-blue)' }}>[{d.phase}]</strong> {d.keywordOrTopic}: {d.decisionMade} — <em>{d.reason}</em>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
